import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, ExternalLink, Sparkles, Copy, Check, X, Send, Plus, Pencil, Calendar } from 'lucide-react';
import DocumentsCard from '../components/DocumentsCard';
import ConversationView, { formatLocalTime } from '../components/ConversationView';
import LeadDiagnosticCard from '../components/LeadDiagnosticCard';

// Fallback when the logged-in user has no per-user Claude project URL set
// (users.claude_project_url). Lands them in a fresh Claude chat, which is
// still functional — the v2 system prompt travels with the copied context.
const CLAUDE_FALLBACK_URL = 'https://claude.ai/new';

const InfoRow = ({ label, value }) => (
  <div style={{ display: 'flex', gap: 12, padding: '6px 0' }}>
    <div style={{ minWidth: 140, fontSize: 13, color: '#6B7280', fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 14, color: '#111827' }}>{value || '—'}</div>
  </div>
);

// Phase 3: USD formatter used on Lead Detail + modal previews.
// Per-deal display uses full formatting with cents ($25,000.00).
// Returns '—' for null/undefined/empty to match InfoRow convention.
const formatUSD = (n) => {
  if (n === null || n === undefined || n === '' || Number.isNaN(Number(n))) return '—';
  const num = Number(n);
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const ClassificationBadge = ({ value }) => {
  const map = { hot: { bg: '#FEE2E2', fg: '#991B1B' }, warm: { bg: '#FEF3C7', fg: '#92400E' }, cold: { bg: '#DBEAFE', fg: '#1E40AF' } };
  const c = map[value] || { bg: '#F3F4F6', fg: '#374151' };
  return <span style={{ background: c.bg, color: c.fg, fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 12, textTransform: 'uppercase' }}>{value}</span>;
};

// Phase 2: configuration for the lead_status dropdown + badge.
// adminOnly hides the option from non-admin users.
// selectable=false means the option doesn't appear in the dropdown
// (we don't transition INTO active — it's a starting state only).
const STATUS_CONFIG = {
  active:              { label: 'Active',              bg: '#F3F4F6', fg: '#374151', adminOnly: false, selectable: false },
  discovery_scheduled: { label: 'Discovery Scheduled', bg: '#DBEAFE', fg: '#1E40AF', adminOnly: false, selectable: true  },
  discovery_done:      { label: 'Discovery Done',      bg: '#BFDBFE', fg: '#1E3A8A', adminOnly: false, selectable: true  },
  proposal_sent:       { label: 'Proposal Sent',       bg: '#EDE9FE', fg: '#5B21B6', adminOnly: true,  selectable: true  },
  negotiation:         { label: 'Negotiation',         bg: '#FEF3C7', fg: '#92400E', adminOnly: true,  selectable: true  },
  closed_won:          { label: 'Closed Won',          bg: '#D1FAE5', fg: '#065F46', adminOnly: false, selectable: true  },
  closed_lost:         { label: 'Closed Lost',         bg: '#FEE2E2', fg: '#991B1B', adminOnly: false, selectable: true  },
  not_interested:      { label: 'Not Interested',      bg: '#F3F4F6', fg: '#374151', adminOnly: false, selectable: true  },
  dead_cold:           { label: 'Dead / Cold',         bg: '#F3F4F6', fg: '#374151', adminOnly: false, selectable: true  },
  parked:              { label: 'Parked',              bg: '#FEF3C7', fg: '#92400E', adminOnly: false, selectable: true  },
};

const StatusBadge = ({ value }) => {
  const c = STATUS_CONFIG[value] || { label: value, bg: '#F3F4F6', fg: '#374151' };
  return <span style={{ background: c.bg, color: c.fg, fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 12 }}>{c.label}</span>;
};

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isAdminOrTL, user, canSeeTeamData } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [showAI, setShowAI] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showProposal, setShowProposal] = useState(false);
  const [proposalValue, setProposalValue] = useState('');
  const [proposalSummary, setProposalSummary] = useState('');
  const [submittingProposal, setSubmittingProposal] = useState(false);
  const [showPark, setShowPark] = useState(false);
  const [parkNurtureUntil, setParkNurtureUntil] = useState('');
  const [parkReason, setParkReason] = useState('');
  const [submittingPark, setSubmittingPark] = useState(false);
  // Phase 2: structured add-message form (replaces the free-text append box).
  const [showAdd, setShowAdd] = useState(false);
  const [addSender, setAddSender] = useState('bde');
  const [addBody, setAddBody] = useState('');
  const [addChannel, setAddChannel] = useState('');
  const [submittingAdd, setSubmittingAdd] = useState(false);
  // Phase 3: inline deal value edit on the Lead Info card
  const [editingDealValue, setEditingDealValue] = useState(false);
  const [dealValueInput, setDealValueInput] = useState('');
  const [savingDealValue, setSavingDealValue] = useState(false);

  // Connection-note tracking inline edit (works even on validated leads).
  const [editingNote, setEditingNote] = useState(false);
  const [noteSentInput, setNoteSentInput] = useState(null); // true | false | null
  const [noteTextInput, setNoteTextInput] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Upwork job URL inline edit (works on validated leads via the metadata-only
  // endpoint — lets BDEs backfill the link on already-added Upwork leads).
  const [editingJobUrl, setEditingJobUrl] = useState(false);
  const [jobUrlInput, setJobUrlInput] = useState('');
  const [savingJobUrl, setSavingJobUrl] = useState(false);
  // Structured conversation (Phase 1) — timestamped message rows for display.
  const [messages, setMessages] = useState([]);

  // AI Workspace prompt is now editable from the Guidelines section (stored as
  // two guidelines rows keyed by category). We fetch the row for this user's
  // role and use it as the system prompt; if the fetch fails or no row exists,
  // buildContext() falls back to the hardcoded text below so the workspace
  // never breaks. `null` = not loaded / use fallback.
  const [promptTemplate, setPromptTemplate] = useState(null);

  const fetchLead = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/leads/${id}`);
      setLead(res.data.lead);
      setMessages(res.data.messages || []);
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to load lead.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLead();
  }, [id]);

  // Load the role-appropriate AI Workspace prompt from Guidelines. Silent on
  // failure — buildContext() falls back to the hardcoded prompt.
  useEffect(() => {
    if (!user?.role) return;
    const category = user.role === 'admin'
      ? 'ai_workspace_prompt_admin'
      : 'ai_workspace_prompt_bde';
    api.get(`/guidelines?category=${category}`)
      .then((r) => {
        const row = (r.data.guidelines || [])[0];
        if (row && row.current_content) setPromptTemplate(row.current_content);
      })
      .catch(() => { /* keep null -> hardcoded fallback */ });
  }, [user?.role]);

  const buildContext = () => {
    if (!lead) return '';
    // AI Workspace prompt split (feature/ai-workspace-prompt-split):
    // role-based system prompt. admin → admin prompt (5 workflow modes,
    // incl. discovery-call prep + proposal review); every other role
    // (team_lead, bde, ...) → BDE prompt (3 modes — no discovery prep or
    // proposal writing, those are Prateek's domain). Both texts are kept
    // verbatim; the only substitution is the "Suggested chat name" line,
    // filled from the lead. This replaces v2's per-user line-1
    // personalization with two fixed, role-specific prompts.
    const isAdminPrompt = user?.role === 'admin';

    const adminPrompt = `You are helping Prateek Arora (Business Head at SoftRadix) work a sales lead.

DO NOT narrate or acknowledge this framing in your replies — just behave according to it.

CONTEXT:
This is a deliberately stateless scaffold. The chats are training data for a future built-in feature in the SoftRadix Sales Tool. Treat each chat as both (a) helping with this specific lead, and (b) helping us learn what context format, prompt structure, and response style produce the best output. If you notice the context is missing something useful or a different prompt would have worked better, say so.

Suggested chat name: "${lead.prospect_name} — ${lead.company}" (Prateek will rename manually if auto-name is off).

WORKFLOW:
Before responding, recommend the single best next action for this lead in one short sentence with the reason. Then list the common modes so Prateek can override:
  1. Draft the next message to the prospect
  2. Prep for a discovery call
  3. Review or build a proposal
  4. Qualification check (worth chasing, or nurture/drop)
  5. Something else specific

Wait for Prateek's choice before drafting.

---

LINKEDIN PROFILE MODEL (read this carefully — this is how SoftRadix actually operates):

SoftRadix uses multiple LinkedIn profiles for outreach. Currently 5 BDEs operate 13 profiles across the team. The sender name visible inside the conversation (Pradeep, Saurav, Komal, Amit, or any other profile identity) is the profile being used, NOT necessarily the actual person behind it. The real BDE owner is in the lead metadata block at the top of this prompt.

What this means in practice:
- A message signed "Pradeep" in the LinkedIn thread might be sent by Shivam using the Pradeep profile.
- Leadership (Prateek, Pradeep, Saurav) does NOT do LinkedIn back-and-forth. They only join for calls AFTER a BDE hands off the lead.
- A promised call from "Prateek" or "Pradeep" in the thread is real — that part (leadership on the call) is genuine. But the LinkedIn messages leading up to it were sent by the BDE operator using that profile.

Therefore:
- Do NOT flag "messages are from Pradeep but BDE owner is Shivam" as an inconsistency. That's the operating model, not a problem.
- Do NOT ask Prateek "should this go out under Pradeep's name or yours?" — match the voice and signature pattern already in the thread, and draft.
- DO continue to flag unbooked promises of leadership calls (Failure Mode #4 below) — that's a real risk regardless of which profile sent the promise.

---

PUSH BACK HONESTLY when the next move looks wrong. The team has consistent failure modes — flag and redirect when you see them:

1. PORTFOLIO-DUMPING BEFORE QUALIFYING
If the user wants to share case studies, portfolio links, or capability descriptions before the prospect has named a specific problem or project, push back. The right next move is one qualifying question, not a brochure.

2. CALL REQUEST WITHOUT A REASON
If the user wants to send a Calendly link or ask for a call without a specific reason tied to something the prospect actually said, push back. Either the call is not needed yet, or the ask must name the thing to be discussed ("to dig into the [X] you mentioned").

3. DUPLICATE FOLLOW-UPS
Read the conversation. If the previous BDE message already asked what the user is about to ask, suggest a different angle, a longer wait, or a step back. Never "just following up" or "circling back".

4. UNBOOKED LEADERSHIP HANDOFFS
If the conversation contains a promise that Prateek or Pradeep will call, but no evidence the call was actually booked (no Calendly confirmation, no time agreed in the messages), flag it. Recommend booking before any more messages go out.

5. CALENDLY DROP-OFFS
If a meeting was scheduled but the prospect did not show, recommend a specific recovery message (acknowledge the miss without guilt-trip, propose a new time).

6. COLD-BUT-INTERESTED MISCLASSIFICATION
If the prospect has shown clear interest (agreed to talk, shared scope, real back-and-forth) but the lead is marked Cold, push back. The lead is likely under-rated.

---

CLASSIFICATION RULE (Hot / Warm / Cold):
- HOT means an active deal in motion (scoping discussion, proposal stage, payment discussion). If those signals are not present, it is not Hot.
- WARM only if the prospect confirmed BOTH (a) a specific project or problem AND (b) their own involvement in the tech or buying decision. If only one is true, it is over-rated — recommend Cold.
- COLD with clear interest signals (specific replies, time committed, scope discussed) is under-rated — recommend Warm.

---

TYPE A / TYPE B DOCTRINE (for prospects replying to the first message):
- Type A = prospect named a specific project or problem. Hand off to Prateek to book a call.
- Type B = prospect replied with curiosity ("tell me more", "let's connect", "send me info") but did not name anything specific. Do NOT book a call yet.

For Type B, send this exact message (do not edit, do not add a Calendly link):
"Sure, happy to chat. What's on your mind — something you're already building, or more of an exploratory conversation?"

If they then reveal a project, hand off. If they confirm exploratory, close politely with:
"Got it. Honestly, without something specific to dig into, a call would probably feel like a generic pitch from both sides. If anything concrete comes up — a project you're scoping, a decision you're weighing — feel free to message me directly when that happens."
Then stop. No follow-ups.

---

FOLLOW-UP RULES:
- Never write "just following up", "bumping this", or "circling back". Every follow-up adds new info or a new angle.
- Follow-ups are shorter than the first message.
- If the prospect viewed the profile but did not reply, wait. Don't send the same day.
- If the prospect said "not interested" or "not now", reply politely and stop. Mark revisit in 3 months.
- Never attach PDFs in LinkedIn messages unless the prospect asked for it.

---

BEST MESSAGE FORMAT (what's converting in our data):
One sentence on what the prospect is building. One sentence on a specific SoftRadix parallel. One sharp question about where the prospect is stuck. Use this when drafting first messages or value follow-ups.

---

CASE STUDIES (reference only when they fit the prospect's actual problem):
- BillPRO.Ai — healthcare AI billing, 25% claim rejection reduction, 15 min/claim saved
- GluCare — digital therapeutics, HbA1c reduced 2.14% in 90 days
- BantingMed — diabetes tracking with Apple Health + Google Health Connect
- FlippBidd — real estate investing, 3D maps, skip trace, Twilio calling, ROI calc
- AskVinny — PropTech, GPT-4 tenant assistant on email + WhatsApp 24/7
- HiBooks — UK accounting, AI receipt scanning, MTD compliance
- Chronifi — personal finance with Sankey diagrams, real wage calculators
- Albatroz — insurance, 95% faster policy generation, 100+ API integrations
- SummarizeX — audio to structured summaries, 12 languages (Deepgram + Gemini)
- Todo Cloud — 500K+ users, Macworld featured
- Leads That Work — AI LinkedIn automation, 80 to 90% prospecting time reduction
- RCW Dashboard — GPT-4o-mini + vector DB for workforce querying
- CID Inspections — two-app inspection ecosystem, GPS + Stripe payouts
- Smarty — Calendly alternative with timezone intelligence
- MadLabs — compliant cannabis e-commerce, age verification
- YTeach — 3.5-year peer tutoring platform, live in app stores
- EstateMate — 5 white-label community management apps (South Africa)
- Christian Questions — native iOS + Android podcast app, live streaming
- Learnistic — content monetization with in-app purchases and analytics
- SheyDance — dance education marketplace, verified instructors, Stripe
- Prime Trainers — trainer-client platform, slot-based subscriptions

Pick one mapping to the prospect's actual stated problem. If nothing maps cleanly, do not force a reference.

---

VOICE AND GUARDRAILS:
- Short, specific, no corporate fluff, no em dashes.
- Never use "comprehensive", "leverage", "streamline", "cutting-edge", "robust", "synergy".
- Messages under 5 lines unless Prateek asks otherwise.
- Never quote pricing.
- Never offer to send proposals via LinkedIn.
- Read drafts aloud before declaring done — if it sounds like a brochure, rewrite.`;

    const bdePrompt = `You are helping a SoftRadix BDE (Amit, Shivam, Nageshwar, Yashika, Abhimanyu, Harman, Komal, or Gurkirat) work a sales lead.

DO NOT narrate or acknowledge this framing in your replies — just behave according to it.

CONTEXT:
This is a deliberately stateless scaffold. The chats are training data for a future built-in feature in the SoftRadix Sales Tool. Treat each chat as both (a) helping with this specific lead, and (b) helping us learn what context format, prompt structure, and response style produce the best output. If you notice the context is missing something useful or a different prompt would have worked better, say so.

Suggested chat name: "${lead.prospect_name} — ${lead.company}" (the BDE will rename manually if auto-name is off).

WORKFLOW:
Before responding, recommend the single best next action for this lead in one short sentence with the reason. Then list the common modes so the BDE can override:
  1. Draft the next message to the prospect
  2. Qualification check (worth chasing, or nurture/drop)
  3. Something else specific

Discovery call prep and proposal writing are Prateek's domain — BDEs do not do those. If the right next step is to hand the lead off for a discovery call, say so and recommend handing off to Prateek rather than offering to prep.

Wait for the BDE's choice before drafting.

---

LINKEDIN PROFILE MODEL (read this carefully — this is how SoftRadix actually operates):

SoftRadix uses multiple LinkedIn profiles for outreach. Currently 5 BDEs operate 13 profiles across the team. The sender name visible inside the conversation (Pradeep, Saurav, Komal, Amit, or any other profile identity) is the profile being used, NOT necessarily the actual person behind it. The real BDE owner is in the lead metadata block at the top of this prompt.

What this means in practice:
- A message signed "Pradeep" in the LinkedIn thread might be sent by Shivam using the Pradeep profile.
- Leadership (Prateek, Pradeep, Saurav) does NOT do LinkedIn back-and-forth. They only join for calls AFTER a BDE hands off the lead.
- A promised call from "Prateek" or "Pradeep" in the thread is real — that part (leadership on the call) is genuine. But the LinkedIn messages leading up to it were sent by the BDE operator using that profile.

Therefore:
- Do NOT flag "messages are from Pradeep but BDE owner is Shivam" as an inconsistency. That's the operating model, not a problem.
- Do NOT ask the BDE "should this go out under Pradeep's name or yours?" — match the voice and signature pattern already in the thread, and draft. The BDE knows which profile they're sending from.
- DO continue to flag unbooked promises of leadership calls (Failure Mode #4 below) — that's a real risk regardless of which profile sent the promise.

---

PUSH BACK HONESTLY when the next move looks wrong. The team has consistent failure modes — flag and redirect when you see them:

1. PORTFOLIO-DUMPING BEFORE QUALIFYING
If the BDE wants to share case studies, portfolio links, or capability descriptions before the prospect has named a specific problem or project, push back. The right next move is one qualifying question, not a brochure.

2. CALL REQUEST WITHOUT A REASON
If the BDE wants to send a Calendly link or ask for a call without a specific reason tied to something the prospect actually said, push back. Either the call is not needed yet, or the ask must name the thing to be discussed ("to dig into the [X] you mentioned").

3. DUPLICATE FOLLOW-UPS
Read the conversation. If the previous BDE message already asked what the BDE is about to ask, suggest a different angle, a longer wait, or a step back. Never "just following up" or "circling back".

4. UNBOOKED LEADERSHIP HANDOFFS
If the conversation contains a promise that Prateek or Pradeep will call, but no evidence the call was actually booked (no Calendly confirmation, no time agreed in the messages), flag it. Recommend booking before any more messages go out.

5. CALENDLY DROP-OFFS
If a meeting was scheduled but the prospect did not show, recommend a specific recovery message (acknowledge the miss without guilt-trip, propose a new time).

6. COLD-BUT-INTERESTED MISCLASSIFICATION
If the prospect has shown clear interest (agreed to talk, shared scope, real back-and-forth) but the lead is marked Cold, push back. The lead is likely under-rated.

---

CLASSIFICATION RULE (Hot / Warm / Cold):
- HOT means an active deal in motion (scoping discussion, proposal stage, payment discussion). If those signals are not present, it is not Hot.
- WARM only if the prospect confirmed BOTH (a) a specific project or problem AND (b) their own involvement in the tech or buying decision. If only one is true, it is over-rated — recommend Cold.
- COLD with clear interest signals (specific replies, time committed, scope discussed) is under-rated — recommend Warm.

---

TYPE A / TYPE B DOCTRINE (for prospects replying to the first message):
- Type A = prospect named a specific project or problem. Hand off to Prateek to book a call.
- Type B = prospect replied with curiosity ("tell me more", "let's connect", "send me info") but did not name anything specific. Do NOT book a call yet.

For Type B, send this exact message (do not edit, do not add a Calendly link):
"Sure, happy to chat. What's on your mind — something you're already building, or more of an exploratory conversation?"

If they then reveal a project, hand off. If they confirm exploratory, close politely with:
"Got it. Honestly, without something specific to dig into, a call would probably feel like a generic pitch from both sides. If anything concrete comes up — a project you're scoping, a decision you're weighing — feel free to message me directly when that happens."
Then stop. No follow-ups.

---

FOLLOW-UP RULES:
- Never write "just following up", "bumping this", or "circling back". Every follow-up adds new info or a new angle.
- Follow-ups are shorter than the first message.
- If the prospect viewed the profile but did not reply, wait. Don't send the same day.
- If the prospect said "not interested" or "not now", reply politely and stop. Mark revisit in 3 months.
- Never attach PDFs in LinkedIn messages unless the prospect asked for it.

---

BEST MESSAGE FORMAT (what's converting in our data):
One sentence on what the prospect is building. One sentence on a specific SoftRadix parallel. One sharp question about where the prospect is stuck. Use this when drafting first messages or value follow-ups.

---

CASE STUDIES (reference only when they fit the prospect's actual problem):
- BillPRO.Ai — healthcare AI billing, 25% claim rejection reduction, 15 min/claim saved
- GluCare — digital therapeutics, HbA1c reduced 2.14% in 90 days
- BantingMed — diabetes tracking with Apple Health + Google Health Connect
- FlippBidd — real estate investing, 3D maps, skip trace, Twilio calling, ROI calc
- AskVinny — PropTech, GPT-4 tenant assistant on email + WhatsApp 24/7
- HiBooks — UK accounting, AI receipt scanning, MTD compliance
- Chronifi — personal finance with Sankey diagrams, real wage calculators
- Albatroz — insurance, 95% faster policy generation, 100+ API integrations
- SummarizeX — audio to structured summaries, 12 languages (Deepgram + Gemini)
- Todo Cloud — 500K+ users, Macworld featured
- Leads That Work — AI LinkedIn automation, 80 to 90% prospecting time reduction
- RCW Dashboard — GPT-4o-mini + vector DB for workforce querying
- CID Inspections — two-app inspection ecosystem, GPS + Stripe payouts
- Smarty — Calendly alternative with timezone intelligence
- MadLabs — compliant cannabis e-commerce, age verification
- YTeach — 3.5-year peer tutoring platform, live in app stores
- EstateMate — 5 white-label community management apps (South Africa)
- Christian Questions — native iOS + Android podcast app, live streaming
- Learnistic — content monetization with in-app purchases and analytics
- SheyDance — dance education marketplace, verified instructors, Stripe
- Prime Trainers — trainer-client platform, slot-based subscriptions

Pick one mapping to the prospect's actual stated problem. If nothing maps cleanly, do not force a reference.

---

VOICE AND GUARDRAILS:
- Short, specific, no corporate fluff, no em dashes.
- Never use "comprehensive", "leverage", "streamline", "cutting-edge", "robust", "synergy".
- Messages under 5 lines unless the BDE asks otherwise.
- Never quote pricing.
- Never offer to send proposals via LinkedIn.
- Read drafts aloud before declaring done — if it sounds like a brochure, rewrite.`;

    // Prefer the DB-editable prompt (from Guidelines), substituting lead tokens;
    // fall back to the hardcoded role prompt above if it hasn't loaded / is absent.
    const fallbackPrompt = isAdminPrompt ? adminPrompt : bdePrompt;
    const prompt = promptTemplate
      ? promptTemplate
          .replace(/\{\{\s*prospect_name\s*\}\}/g, lead.prospect_name || '')
          .replace(/\{\{\s*company\s*\}\}/g, lead.company || '')
      : fallbackPrompt;

    const details = [
      `Prospect: ${lead.prospect_name}`,
      `Company: ${lead.company}`,
      `Industry: ${lead.industry || '—'}`,
      `LinkedIn: ${lead.linkedin_url}`,
      `Classification: ${lead.classification}${lead.original_classification && lead.original_classification !== lead.classification ? ` (originally ${lead.original_classification})` : ''}`,
      `Validation status: ${lead.validation_status}`,
      `Lead date: ${lead.lead_date ? new Date(lead.lead_date).toLocaleDateString() : '—'}`,
      `BDE owner: ${lead.bde_name}`,
      lead.validated_by_name ? `Validated by: ${lead.validated_by_name}` : null,
      lead.reclassification_note ? `Reclassification note: ${lead.reclassification_note}` : null,
    ].filter(x => x !== null).join('\n');

    // Conversation block. Prefer the structured `lead_messages` rows (Phase 1) —
    // they carry per-message timestamps and a clean sender attribution, which is
    // what the page itself renders. The raw `lead.notes` blob is the fallback for
    // leads whose messages haven't been synced (the read-model is derived from
    // notes, so this should be rare) — without it, older leads would copy empty.
    //
    // Timestamps matter to the prompt: several failure modes it checks for are
    // time-shaped (duplicate follow-ups, "wait, don't send the same day",
    // unbooked leadership calls going stale). Sending the untimed notes blob
    // meant Claude was judging cadence blind.
    const conversation = messages.length > 0
      ? messages.map((m) => {
          const who = m.sender === 'bde' ? 'BDE' : 'Prospect';
          const channel = m.meta ? ` ${m.meta}` : '';
          // Backfilled pre-timestamping rows have created_at NULL -> no stamp.
          const time = formatLocalTime(m.created_at);
          const stamp = time ? ` [${time}]` : '';
          return `${who}${channel}${stamp}: ${m.body}`;
        }).join('\n\n')
      : (lead.notes || '(No conversation logged in the lead.)');

    // Name the timezone so Claude reads the stamps correctly instead of
    // assuming UTC. Falls back silently on browsers without resolvedOptions.
    let tzNote = '';
    if (messages.length > 0) {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) tzNote = ` (timestamps in ${tz}; messages without a timestamp predate message-level tracking)`;
      } catch (e) { /* leave tzNote empty */ }
    }

    return `${prompt}\n\n--- Lead details ---\n${details}\n\n--- Conversation so far${tzNote} ---\n${conversation}`;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildContext());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      alert('Could not copy to clipboard. Please select and copy manually.');
    }
  };

  const handleAddMessage = async () => {
    if (!addBody.trim()) {
      toast.error('Please enter the message.');
      return;
    }
    setSubmittingAdd(true);
    try {
      const payload = { sender: addSender, body: addBody.trim() };
      if (addChannel) payload.channel = addChannel;
      await api.post(`/leads/${id}/messages`, payload);
      toast.success('Message added.');
      setShowAdd(false);
      setAddBody('');
      setAddChannel('');
      await fetchLead();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add message.');
    } finally {
      setSubmittingAdd(false);
    }
  };

  // Returns true on success so the inline editor can close itself.
  const handleEditMessage = async (msgId, body) => {
    try {
      await api.put(`/lead-messages/${msgId}`, { body });
      toast.success('Message updated.');
      await fetchLead();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update message.');
      return false;
    }
  };

  const handleDeleteMessage = async (msgId) => {
    try {
      await api.delete(`/lead-messages/${msgId}`);
      toast.success('Message deleted.');
      await fetchLead();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete message.');
    }
  };

  // Phase 2: unified status-change handler routes selections to the right path:
  //   - proposal_sent    → existing modal (collects value + summary)
  //   - terminal stops   → confirm + stop-sequence with mapped reason
  //   - in-flight states → direct API call, success toast
  // Backend enforces permissions; frontend just attempts and surfaces errors.
  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    // Reset the dropdown immediately — it's an action picker, not a value binding.
    e.target.value = '';
    if (!newStatus) return;

    // Proposal sent has its own modal for proposal_value + summary.
    if (newStatus === 'proposal_sent') {
      setShowProposal(true);
      return;
    }

    // Parked has its own modal for nurture_until + optional reason.
    if (newStatus === 'parked') {
      setShowPark(true);
      return;
    }

    // Terminal stops — confirm before applying.
    if (['closed_won', 'closed_lost', 'not_interested', 'dead_cold'].includes(newStatus)) {
      const reasonMap = {
        closed_won: 'closed_won',
        closed_lost: 'lost',
        not_interested: 'not_interested',
        dead_cold: 'dead',
      };
      const label = STATUS_CONFIG[newStatus].label;
      if (!window.confirm(`Mark "${lead.prospect_name}" as ${label}? This will stop all follow-ups.`)) return;
      try {
        await api.post(`/leads/${id}/stop-sequence`, { reason: reasonMap[newStatus] });
        toast.success(`Lead marked as ${label}. Follow-ups stopped.`);
        await fetchLead();
      } catch (err) {
        toast.error(err.response?.data?.error || `Failed to mark as ${label}.`);
      }
      return;
    }

    // Forward in-flight transitions — direct API call, no confirm.
    if (['discovery_scheduled', 'discovery_done', 'negotiation'].includes(newStatus)) {
      const endpointMap = {
        discovery_scheduled: 'mark-discovery-scheduled',
        discovery_done: 'mark-discovery-done',
        negotiation: 'mark-negotiation',
      };
      const label = STATUS_CONFIG[newStatus].label;
      try {
        await api.post(`/leads/${id}/${endpointMap[newStatus]}`);
        toast.success(`Lead marked as ${label}.`);
        await fetchLead();
      } catch (err) {
        toast.error(err.response?.data?.error || `Failed to mark as ${label}.`);
      }
    }
  };

  const handleMarkProposalSent = async () => {
    if (!proposalValue.trim() || !proposalSummary.trim()) {
      toast.error('Both deal value and summary are required.');
      return;
    }
    const dealValueNum = Number(proposalValue);
    if (!Number.isFinite(dealValueNum) || dealValueNum < 0) {
      toast.error('Deal value must be a non-negative number.');
      return;
    }
    setSubmittingProposal(true);
    try {
      await api.post(`/leads/${id}/mark-proposal-sent`, {
        deal_value: dealValueNum,
        proposal_summary: proposalSummary.trim()
      });
      toast.success('Proposal marked sent. Type 3 follow-up sequence started.');
      setShowProposal(false);
      setProposalValue('');
      setProposalSummary('');
      await fetchLead(); // refresh lead to show updated status
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to mark proposal sent.');
    } finally {
      setSubmittingProposal(false);
    }
  };

  const handleMarkParked = async () => {
    if (!parkNurtureUntil) {
      toast.error('Wake date is required.');
      return;
    }
    const wakeDate = new Date(parkNurtureUntil);
    if (isNaN(wakeDate.getTime())) {
      toast.error('Wake date must be a valid date.');
      return;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (wakeDate <= today) {
      toast.error('Wake date must be in the future.');
      return;
    }
    setSubmittingPark(true);
    try {
      await api.post(`/leads/${id}/mark-parked`, {
        nurture_until: parkNurtureUntil,
        park_reason: parkReason.trim() || null
      });
      toast.success(`Lead parked. Will wake on ${parkNurtureUntil}.`);
      setShowPark(false);
      setParkNurtureUntil('');
      setParkReason('');
      await fetchLead();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to park lead.');
    } finally {
      setSubmittingPark(false);
    }
  };

  // Phase 3: Lead Info card inline deal value edit
  const handleStartEditDealValue = () => {
    setDealValueInput(lead.deal_value !== null && lead.deal_value !== undefined ? String(lead.deal_value) : '');
    setEditingDealValue(true);
  };

  const handleCancelEditDealValue = () => {
    setEditingDealValue(false);
    setDealValueInput('');
  };

  const handleSaveDealValue = async () => {
    // Empty string clears the value (null); otherwise must parse to a non-negative number.
    const trimmed = dealValueInput.trim();
    let payloadValue;
    if (trimmed === '') {
      payloadValue = null;
    } else {
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed) || parsed < 0) {
        toast.error('Deal value must be a non-negative number, or empty to clear.');
        return;
      }
      payloadValue = parsed;
    }
    setSavingDealValue(true);
    try {
      await api.put(`/leads/${id}`, { deal_value: payloadValue });
      toast.success(payloadValue === null ? 'Deal value cleared.' : `Deal value set to ${formatUSD(payloadValue)}.`);
      setEditingDealValue(false);
      setDealValueInput('');
      await fetchLead();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save deal value.');
    } finally {
      setSavingDealValue(false);
    }
  };

  // Connection-note tracking inline edit
  const handleStartEditNote = () => {
    setNoteSentInput(lead.is_note_sent === null || lead.is_note_sent === undefined ? null : !!lead.is_note_sent);
    setNoteTextInput(lead.connection_note || '');
    setEditingNote(true);
  };

  const handleCancelEditNote = () => {
    setEditingNote(false);
    setNoteSentInput(null);
    setNoteTextInput('');
  };

  const handleSaveNote = async () => {
    if (noteSentInput === true && !noteTextInput.trim()) {
      toast.error('Please paste the note message.');
      return;
    }
    // Flipping a previously-Yes lead to No clears the saved note — confirm first.
    const wasYes = lead.is_note_sent === 1 || lead.is_note_sent === true;
    if (wasYes && noteSentInput === false && (lead.connection_note || '').trim()) {
      if (!window.confirm('Switching to "No" will clear the saved connection note. Continue?')) return;
    }
    setSavingNote(true);
    try {
      await api.put(`/leads/${id}/connection-note`, {
        is_note_sent: noteSentInput,
        connection_note: noteSentInput === true ? noteTextInput.trim() : null,
      });
      toast.success('Connection-note tracking updated.');
      setEditingNote(false);
      await fetchLead();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update.');
    } finally {
      setSavingNote(false);
    }
  };

  // Upwork job URL inline edit
  const handleStartEditJobUrl = () => {
    setJobUrlInput(lead.upwork_job_url || '');
    setEditingJobUrl(true);
  };

  const handleCancelEditJobUrl = () => {
    setEditingJobUrl(false);
    setJobUrlInput('');
  };

  const handleSaveJobUrl = async () => {
    if (!jobUrlInput.trim()) {
      toast.error('Please paste the Upwork job URL.');
      return;
    }
    setSavingJobUrl(true);
    try {
      await api.put(`/leads/${id}/upwork-job-url`, { upwork_job_url: jobUrlInput.trim() });
      toast.success('Upwork job URL updated.');
      setEditingJobUrl(false);
      await fetchLead();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update.');
    } finally {
      setSavingJobUrl(false);
    }
  };

  if (loading) return <div className="empty-state"><p>Loading lead...</p></div>;
  if (err) return <div className="empty-state"><p style={{ color: 'var(--red)' }}>{err}</p><button onClick={() => navigate('/leads')} className="btn btn-secondary" style={{ marginTop: 12 }}>Back to Leads</button></div>;
  if (!lead) return null;

  const conversationText = lead.notes || '';
  // Phase 2: status dropdown visibility — anyone who can edit the lead can change status.
  // (Backend enforces specific transition permissions per endpoint.)
  const canChangeStatus = (user?.role === 'admin' || user?.role === 'team_lead') || (user?.id === lead.bde_id);
  const canAppendNotes = canChangeStatus;
  const isTerminalState = ['closed_won', 'closed_lost', 'not_interested', 'dead_cold'].includes(lead.lead_status);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <button onClick={() => navigate('/leads')} className="btn btn-secondary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <ArrowLeft size={14} /> Back to Leads
          </button>
          <h2 style={{ marginBottom: 4 }}>{lead.prospect_name}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#6B7280', fontSize: 14, flexWrap: 'wrap' }}>
            <span>{lead.company}</span>
            <span>·</span>
            <ClassificationBadge value={lead.classification} />
            {!!lead.is_ai_rescue && (
              <span
                className="badge"
                style={{ fontSize: 11, fontWeight: 700, background: 'rgba(124,58,237,0.12)', color: '#7C3AED',
                         display: 'inline-flex', alignItems: 'center', gap: 4 }}
                title={lead.ai_rescue_source
                  ? `AI Rescue lead — found via ${lead.ai_rescue_source === 'Other' ? (lead.ai_rescue_source_other || 'Other') : lead.ai_rescue_source}`
                  : 'AI Rescue lead'}
              >
                <Sparkles size={11} /> AI Rescue
              </span>
            )}
            {(lead.source === 'upwork_inbound' || lead.source === 'upwork_research') && (
              <span
                className="badge badge-blue"
                style={{ fontSize: 11, fontWeight: 600 }}
                title={lead.source === 'upwork_inbound'
                  ? 'Sourced from the Upwork referral pipeline'
                  : 'Prospected on Upwork by the BDE'}
              >
                via Upwork
              </span>
            )}
            <span style={{ fontSize: 12, color: lead.validation_status === 'validated' ? '#166534' : '#92400E' }}>
              {lead.validation_status === 'validated' ? 'Validated' : 'Pending review'}
            </span>
            {lead.lead_status && lead.lead_status !== 'active' && (
              <>
                <span>·</span>
                <StatusBadge value={lead.lead_status} />
              </>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {canChangeStatus && (
            <select
              onChange={handleStatusChange}
              value=""
              disabled={isTerminalState}
              style={{
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: 6,
                fontSize: 14,
                background: isTerminalState ? '#F3F4F6' : 'white',
                color: isTerminalState ? '#9CA3AF' : '#111827',
                cursor: isTerminalState ? 'not-allowed' : 'pointer',
                minWidth: 180,
              }}
              title={isTerminalState ? 'This lead is in a terminal state; status changes are locked.' : 'Change the lead status'}
            >
              <option value="">
                {isTerminalState
                  ? `Locked — ${STATUS_CONFIG[lead.lead_status]?.label || lead.lead_status}`
                  : 'Change status…'}
              </option>
              {Object.entries(STATUS_CONFIG)
                .filter(([, cfg]) => cfg.selectable && (!cfg.adminOnly || isAdmin))
                .map(([key, cfg]) => (
                  <option key={key} value={key}>{cfg.label}</option>
                ))}
            </select>
          )}
          {canAppendNotes && (
            <button onClick={() => setShowAdd(true)} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Plus size={14} /> Add Message
            </button>
          )}
          {/* AI Workspace is available to every authenticated user
              (admin, team_lead, bde). The per-user Claude project URL is
              resolved on the modal's "Open Claude project" button. */}
          <button onClick={() => setShowAI(true)} className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Sparkles size={14} /> AI Workspace
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 12 }}>Lead Info</h3>
        <InfoRow label="BDE owner" value={lead.bde_name} />
        <InfoRow label="Industry" value={lead.industry} />
        <InfoRow label="Lead date" value={lead.lead_date ? new Date(lead.lead_date).toLocaleDateString() : null} />
        <InfoRow label="LinkedIn" value={
          <a href={lead.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--blue)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            Open profile <ExternalLink size={12} />
          </a>
        } />
        {/* Connection-note tracking (editable via the Leads edit form). */}
        {/* Connection-note tracking — inline editable even on validated leads
            (uses the metadata-only /connection-note endpoint, exempt from the
            validation lock). canChangeStatus = BDE on own, admin/TL on any. */}
        <div style={{ display: 'flex', gap: 12, padding: '6px 0', alignItems: 'flex-start' }}>
          <div style={{ minWidth: 140, fontSize: 13, color: '#6B7280', fontWeight: 500, paddingTop: 6 }}>Note sent?</div>
          {editingNote ? (
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                {[{ v: true, l: 'Yes' }, { v: false, l: 'No' }, { v: null, l: 'Not set' }].map((o) => (
                  <button
                    key={String(o.v)}
                    type="button"
                    onClick={() => setNoteSentInput(o.v)}
                    className="badge badge-gray"
                    style={{
                      cursor: 'pointer', border: 'none', padding: '6px 14px', fontSize: 13, fontWeight: 600,
                      opacity: noteSentInput === o.v ? 1 : 0.5,
                      outline: noteSentInput === o.v ? '2px solid var(--navy)' : 'none', outlineOffset: 2,
                    }}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
              {noteSentInput === true && (
                <textarea
                  value={noteTextInput}
                  onChange={(e) => setNoteTextInput(e.target.value)}
                  rows={3}
                  placeholder="Paste the exact note message you sent with the connection request"
                  style={{ width: '100%', marginTop: 8, padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: 4, fontSize: 14 }}
                />
              )}
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button onClick={handleSaveNote} disabled={savingNote} className="btn btn-primary" style={{ padding: '4px 10px', fontSize: 12 }}>
                  {savingNote ? '…' : 'Save'}
                </button>
                <button onClick={handleCancelEditNote} disabled={savingNote} className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 14, color: '#111827', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {lead.is_note_sent === null || lead.is_note_sent === undefined ? 'Not set' : (lead.is_note_sent ? 'Yes' : 'No')}
              {canChangeStatus && (
                <button onClick={handleStartEditNote} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 2, display: 'inline-flex' }} title="Edit note-sent">
                  <Pencil size={12} />
                </button>
              )}
            </div>
          )}
        </div>
        {!editingNote && !!lead.is_note_sent && lead.connection_note && (
          <InfoRow label="Connection note" value={lead.connection_note} />
        )}
        {!!lead.is_ai_rescue && lead.ai_rescue_source && (
          <InfoRow
            label="AI Rescue source"
            value={lead.ai_rescue_source === 'Other'
              ? `Other — ${lead.ai_rescue_source_other || 'unspecified'}`
              : lead.ai_rescue_source}
          />
        )}

        {/* Phase 3: Deal Value — inline editable. canChangeStatus reuses the
            same permission as the status dropdown (BDE on own, admin/TL on any).
            Locked when current state is terminal (matches dropdown lock), EXCEPT
            for admin: admins can correct deal value on any lead, including closed/
            terminal ones, without reopening it. Backend updateLead already permits
            this (reviewer path has no terminal block). */}
        <div style={{ display: 'flex', gap: 12, padding: '6px 0', alignItems: 'center' }}>
          <div style={{ minWidth: 140, fontSize: 13, color: '#6B7280', fontWeight: 500 }}>Deal value</div>
          {editingDealValue ? (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 1 }}>
              <span style={{ fontSize: 14, color: '#6B7280' }}>$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                autoFocus
                value={dealValueInput}
                onChange={(e) => setDealValueInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveDealValue();
                  if (e.key === 'Escape') handleCancelEditDealValue();
                }}
                placeholder="0.00"
                disabled={savingDealValue}
                style={{ padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: 4, fontSize: 14, width: 140 }}
              />
              <button
                onClick={handleSaveDealValue}
                disabled={savingDealValue}
                className="btn btn-primary"
                style={{ padding: '4px 10px', fontSize: 12 }}
              >
                {savingDealValue ? '…' : 'Save'}
              </button>
              <button
                onClick={handleCancelEditDealValue}
                disabled={savingDealValue}
                className="btn btn-secondary"
                style={{ padding: '4px 10px', fontSize: 12 }}
              >
                Cancel
              </button>
              <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 4 }}>Empty to clear · Enter saves · Esc cancels</span>
            </div>
          ) : (
            <div style={{ fontSize: 14, color: '#111827', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {formatUSD(lead.deal_value)}
              {canChangeStatus && (!isTerminalState || isAdmin) && (
                <button
                  onClick={handleStartEditDealValue}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 2, display: 'inline-flex' }}
                  title="Edit deal value"
                >
                  <Pencil size={12} />
                </button>
              )}
            </div>
          )}
        </div>

        {lead.lead_status === 'proposal_sent' && (
          <>
            {lead.proposal_value && (
              <InfoRow label="Proposal value (legacy)" value={lead.proposal_value} />
            )}
            <InfoRow label="Proposal summary" value={lead.proposal_summary} />
            <InfoRow label="Proposal sent on" value={lead.proposal_sent_at ? new Date(lead.proposal_sent_at).toLocaleDateString() : null} />
          </>
        )}
        {lead.original_classification && lead.original_classification !== lead.classification && (
          <InfoRow label="Originally classified" value={lead.original_classification.toUpperCase()} />
        )}
        {lead.reclassification_note && <InfoRow label="Reclassification note" value={lead.reclassification_note} />}
        {lead.validated_by_name && <InfoRow label="Validated by" value={lead.validated_by_name} />}

        {/* Upwork audit rows. The job-URL link shows for any Upwork-sourced
            lead (inbound or research). The referral link is inbound-only —
            admin/TL/viewer get a clickable referral page, BDE owners see just
            the reference text (they don't have referral-page access). */}
        {(lead.source === 'upwork_inbound' || lead.source === 'upwork_research') && (
          <div style={{ display: 'flex', gap: 12, padding: '6px 0', alignItems: 'flex-start' }}>
            <div style={{ minWidth: 140, fontSize: 13, color: '#6B7280', fontWeight: 500, paddingTop: 6 }}>Upwork job</div>
            {editingJobUrl ? (
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  value={jobUrlInput}
                  onChange={(e) => setJobUrlInput(e.target.value)}
                  placeholder="https://www.upwork.com/jobs/..."
                  style={{ width: '100%', padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: 4, fontSize: 14 }}
                />
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button onClick={handleSaveJobUrl} disabled={savingJobUrl} className="btn btn-primary" style={{ padding: '4px 10px', fontSize: 12 }}>
                    {savingJobUrl ? '…' : 'Save'}
                  </button>
                  <button onClick={handleCancelEditJobUrl} disabled={savingJobUrl} className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 14, color: '#111827', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {lead.upwork_job_url ? (
                  <a href={lead.upwork_job_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--blue)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    Open job posting <ExternalLink size={12} />
                  </a>
                ) : (
                  <span style={{ color: '#92400E' }}>Not set</span>
                )}
                {canChangeStatus && (
                  <button onClick={handleStartEditJobUrl} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 2, display: 'inline-flex' }} title="Edit Upwork job URL">
                    <Pencil size={12} />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {(lead.source === 'upwork_inbound' || lead.source === 'upwork_research') && lead.referral_id && (
          <InfoRow
            label="From Upwork referral"
            value={
              canSeeTeamData ? (
                <a
                  href={`/upwork-referrals/${lead.referral_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--blue)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  Referral #{lead.referral_id} <ExternalLink size={12} />
                </a>
              ) : (
                <span>Referral #{lead.referral_id}</span>
              )
            }
          />
        )}
      </div>

      {/* Phase 4: prospect-shared documents (scope docs, NDAs, etc.) */}
      <DocumentsCard leadId={lead.id} leadBdeId={lead.bde_id} />

      {lead.ai_reviewed_at && (() => {
        const agrees = lead.classification === lead.ai_review_suggested_classification;
        const actions = Array.isArray(lead.ai_review_upgrade_actions)
          ? lead.ai_review_upgrade_actions
          : (() => { try { return JSON.parse(lead.ai_review_upgrade_actions || '[]'); } catch { return []; } })();
        const bg = agrees ? '#ECFDF5' : '#FFFBEB';
        const accent = agrees ? '#10B981' : '#F59E0B';
        const textColor = agrees ? '#065F46' : '#92400E';
        return (
          <div className="card" style={{ marginBottom: 16, background: bg, borderLeft: `4px solid ${accent}` }}>
            <h3 style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Sparkles size={16} /> AI Insight
            </h3>
            <div style={{ marginBottom: 12, fontSize: 14, color: textColor }}>
              {agrees ? (
                <>✓ AI confirms this lead is correctly classified as <strong>{lead.classification?.toUpperCase()}</strong></>
              ) : (
                <>⚠ AI suggests this might be <strong>{lead.ai_review_suggested_classification?.toUpperCase()}</strong> instead of <strong>{lead.classification?.toUpperCase()}</strong> (confidence: {lead.ai_review_confidence})</>
              )}
            </div>
            {lead.ai_review_reasoning && (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#374151', fontStyle: 'italic' }}>
                {lead.ai_review_reasoning}
              </div>
            )}
            {actions.length > 0 && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  Questions to Ask Next
                </div>
                <ul style={{ paddingLeft: 20, margin: 0 }}>
                  {actions.map((action, i) => (
                    <li key={i} style={{ marginBottom: 6, fontSize: 13, color: '#1F2937' }}>{action}</li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 12 }}>
              AI reviewed {new Date(lead.ai_reviewed_at).toLocaleString()} · Suggestion only — your call.
            </div>
          </div>
        );
      })()}

      <div className="card">
        <h3 style={{ marginBottom: 12 }}>Conversation</h3>
        <ConversationView
          text={conversationText}
          messages={messages}
          canEdit={canAppendNotes}
          onEditMessage={handleEditMessage}
          onDeleteMessage={handleDeleteMessage}
        />
      </div>

      {/* AI Diagnostic generation is admin/TL-controlled: only admin or
          team_lead can trigger generation (canRegenerate={isAdminOrTL}). bde
          and viewer see the latest diagnostic, full history, and Copy buttons —
          they just can't run a new one. The backend POST is also
          requireAdminOrTL-gated, so this is UX, not the guard. */}
      <LeadDiagnosticCard
        leadId={lead.id}
        canRegenerate={isAdminOrTL}
        prospectName={lead.prospect_name}
        company={lead.company}
      />

      {showAdd && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowAdd(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}
        >
          <div style={{ background: '#FFFFFF', borderRadius: 8, maxWidth: 560, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: 16, borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><Plus size={18} /> Add Message</h3>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>One message at a time — it's timestamped now, in your local time.</div>
              </div>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}><X size={20} /></button>
            </div>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, overflow: 'auto' }}>
              <div className="form-group">
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>From</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[{ v: 'bde', l: 'BDE' }, { v: 'prospect', l: 'Prospect' }].map((o) => (
                    <button key={o.v} type="button" onClick={() => setAddSender(o.v)}
                      className="badge badge-gray"
                      style={{ cursor: 'pointer', border: 'none', padding: '8px 16px', fontSize: 14, fontWeight: 600,
                        opacity: addSender === o.v ? 1 : 0.5,
                        outline: addSender === o.v ? '2px solid var(--navy)' : 'none', outlineOffset: 2 }}>
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Channel (optional)</label>
                <select value={addChannel} onChange={(e) => setAddChannel(e.target.value)}
                  style={{ padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14 }}>
                  <option value="">None</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="email">Email</option>
                </select>
              </div>
              <div className="form-group">
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Message</label>
                <textarea
                  value={addBody}
                  onChange={(e) => setAddBody(e.target.value)}
                  rows={6}
                  placeholder="Paste the exact message…"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14, fontFamily: 'inherit' }}
                />
              </div>
            </div>
            <div style={{ padding: 16, borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowAdd(false)} className="btn btn-secondary" disabled={submittingAdd}>Cancel</button>
              <button onClick={handleAddMessage} className="btn btn-primary" disabled={submittingAdd}>
                {submittingAdd ? 'Saving...' : 'Add Message'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showProposal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowProposal(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}
        >
          <div style={{ background: '#FFFFFF', borderRadius: 8, maxWidth: 560, width: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: 16, borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><Send size={18} /> Mark Proposal Sent</h3>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>Starts the post-proposal follow-up sequence (Day 2/5/10/17/25).</div>
              </div>
              <button onClick={() => setShowProposal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}><X size={20} /></button>
            </div>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-group">
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Deal value (USD)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 14, color: '#6B7280' }}>$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={proposalValue}
                    onChange={(e) => setProposalValue(e.target.value)}
                    placeholder="25000"
                    style={{ flex: 1, padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14 }}
                  />
                </div>
                {proposalValue && Number.isFinite(Number(proposalValue)) && Number(proposalValue) >= 0 && (
                  <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                    Preview: {formatUSD(proposalValue)}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>One-line summary of what the proposal covers</label>
                <textarea
                  value={proposalSummary}
                  onChange={(e) => setProposalSummary(e.target.value)}
                  rows={3}
                  placeholder="e.g. Phase 1 — 10 modules including marketplace, community, content discovery"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14, fontFamily: 'inherit' }}
                />
              </div>
            </div>
            <div style={{ padding: 16, borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowProposal(false)} className="btn btn-secondary" disabled={submittingProposal}>Cancel</button>
              <button onClick={handleMarkProposalSent} className="btn btn-primary" disabled={submittingProposal}>
                {submittingProposal ? 'Submitting...' : 'Mark Proposal Sent'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPark && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowPark(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}
        >
          <div style={{ background: '#FFFFFF', borderRadius: 8, maxWidth: 560, width: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: 16, borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><Calendar size={18} /> Park Lead</h3>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>Pauses all follow-ups until the wake date. Lead auto-resumes with a fresh Type 1 cadence on that day.</div>
              </div>
              <button onClick={() => setShowPark(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}><X size={20} /></button>
            </div>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-group">
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Wake date</label>
                <input
                  type="date"
                  value={parkNurtureUntil}
                  onChange={(e) => setParkNurtureUntil(e.target.value)}
                  min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14 }}
                />
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                  Lead flips back to active and starts a fresh cadence on this date.
                </div>
              </div>
              <div className="form-group">
                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Reason (optional)</label>
                <textarea
                  value={parkReason}
                  onChange={(e) => setParkReason(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="e.g. Prospect said revisit after Q4 fundraise"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 14, fontFamily: 'inherit' }}
                />
              </div>
            </div>
            <div style={{ padding: 16, borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowPark(false)} className="btn btn-secondary" disabled={submittingPark}>Cancel</button>
              <button onClick={handleMarkParked} className="btn btn-primary" disabled={submittingPark}>
                {submittingPark ? 'Parking...' : 'Park Lead'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showAI && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowAI(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}
        >
          <div style={{ background: '#FFFFFF', borderRadius: 8, maxWidth: 720, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: 16, borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><Sparkles size={18} /> AI Workspace</h3>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>Copy the context, then open Claude to discuss this lead.</div>
              </div>
              <button onClick={() => setShowAI(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}><X size={20} /></button>
            </div>
            <div style={{ padding: 16, overflow: 'auto', flex: 1 }}>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, padding: 12, fontSize: 12, color: '#374151', maxHeight: 400, overflow: 'auto', fontFamily: 'inherit' }}>
                {buildContext()}
              </pre>
            </div>
            <div style={{ padding: 16, borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={handleCopy} className="btn btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy context</>}
              </button>
              <a href={user?.claude_project_url || CLAUDE_FALLBACK_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <ExternalLink size={14} /> Open Claude project
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadDetail;
